import React, { useEffect, useState } from 'react';
import Icon from '../../components/openplay/OpenPlayIcon';
import BottomTabNavigation from '../../components/openplay/BottomTabNavigation';
import { supabase } from '../../lib/supabaseClient';

const UserProfileManagementPage = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [isLoading, setIsLoading] = useState(false);
  const [userId, setUserId] = useState('');

  const [username, setUsername] = useState('');
  const [bio, setBio] = useState('');
  const [location, setLocation] = useState('');
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarUrl, setAvatarUrl] = useState('');
  const [avatarPath, setAvatarPath] = useState('');
  const [avatarDeleted, setAvatarDeleted] = useState(false);

  const [userType, setUserType] = useState('regular');
  const [orgName, setOrgName] = useState('');
  const [email, setEmail] = useState('');

  const [selectedSports, setSelectedSports] = useState([]);
  const [skillLevels, setSkillLevels] = useState({
    soccer: 'beginner',
    basketball: 'beginner',
  });
  const [searchRadius, setSearchRadius] = useState(10);

  const compressImageToUnder1MB = async (file) => {
    const MAX_BYTES = 1024 * 1024;
    if (!file || !(file instanceof File)) return file;
    if (!String(file?.type || '').startsWith('image/')) return file;
    if (file.size <= MAX_BYTES) return file;

    const blobUrl = URL.createObjectURL(file);
    try {
      let bitmap;
      try {
        bitmap = await createImageBitmap(file);
      } catch {
        const img = await new Promise((resolve, reject) => {
          const el = new Image();
          el.onload = () => resolve(el);
          el.onerror = () => reject(new Error('Failed to load image'));
          el.src = blobUrl;
        });
        bitmap = img;
      }

      const origWidth = bitmap?.width || bitmap?.naturalWidth || 0;
      const origHeight = bitmap?.height || bitmap?.naturalHeight || 0;
      if (!origWidth || !origHeight) return file;

      // Reduce resolution for huge images first.
      const maxSide = 1600;
      const scale = Math.min(1, maxSide / Math.max(origWidth, origHeight));
      const targetWidth = Math.max(1, Math.round(origWidth * scale));
      const targetHeight = Math.max(1, Math.round(origHeight * scale));

      const canvas = document.createElement('canvas');
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) return file;
      ctx.drawImage(bitmap, 0, 0, targetWidth, targetHeight);

      const toBlob = (type, quality) =>
        new Promise((resolve) => {
          canvas.toBlob((b) => resolve(b), type, quality);
        });

      // Try WebP first, then JPEG.
      const candidates = ['image/webp', 'image/jpeg'];
      for (const type of candidates) {
        let quality = 0.82;
        let out = await toBlob(type, quality);
        while (out && out.size > MAX_BYTES && quality > 0.35) {
          quality -= 0.08;
          out = await toBlob(type, quality);
        }
        if (out && out.size <= MAX_BYTES) {
          const ext = type === 'image/webp' ? 'webp' : 'jpg';
          return new File([out], `avatar.${ext}`, { type });
        }
      }

      // Fallback: return best effort.
      const fallback = await toBlob('image/jpeg', 0.6);
      if (fallback) {
        return new File([fallback], 'avatar.jpg', { type: 'image/jpeg' });
      }
      return file;
    } finally {
      try {
        URL.revokeObjectURL(blobUrl);
      } catch {
        // ignore
      }
    }
  };

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;

    const loadProfile = async () => {
      setIsLoading(true);
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const user = sessionData?.session?.user;
        if (!user?.id) {
          setIsLoading(false);
          return;
        }

        setUserId(user.id);
        setEmail(String(user?.email || ''));

        const meta = user?.user_metadata || {};
        const metaSports = Array.isArray(meta?.sports) ? meta.sports : [];
        const nextAvatarUrl = String(meta?.avatar_url || '').trim();
        const nextAvatarPath = String(meta?.avatar_path || '').trim();

        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('name, type, organization_name, bio, location, skill_levels, search_radius')
          .eq('id', user.id)
          .maybeSingle();

        if (profileError) {
          setIsLoading(false);
          return;
        }

        const nextName = String(profile?.name || meta?.username || '').trim();
        const nextType = String(profile?.type || meta?.type || 'regular');
        const nextOrgName = String(profile?.organization_name || meta?.organization_name || '').trim();
        const nextBio = String(profile?.bio || meta?.bio || '').trim();
        const nextLocation = String(profile?.location || meta?.location || '').trim();
        const nextSkillLevels =
          profile?.skill_levels && typeof profile.skill_levels === 'object'
            ? profile.skill_levels
            : meta?.skill_levels && typeof meta.skill_levels === 'object'
              ? meta.skill_levels
              : {};
        const nextSearchRadiusRaw = profile?.search_radius ?? meta?.search_radius;
        const nextSearchRadius = Number.isFinite(Number(nextSearchRadiusRaw)) ? Number(nextSearchRadiusRaw) : 10;

        setUsername(nextName);
        setUserType(nextType);
        setOrgName(nextOrgName);
        setBio(nextBio);
        setLocation(nextLocation);
        setSelectedSports(metaSports);
        setSkillLevels((prev) => ({ ...(prev || {}), ...(nextSkillLevels || {}) }));
        setSearchRadius(nextSearchRadius);
        setAvatarUrl(nextAvatarUrl);
        setAvatarPath(nextAvatarPath);
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, [isHydrated]);

  const sportOptions = [
    { value: 'pickleball', label: '피클볼', icon: 'FireIcon' },
    { value: 'soccer', label: '축구', icon: 'FireIcon' },
    { value: 'basketball', label: '농구', icon: 'FireIcon' },
    { value: 'tennis', label: '테니스', icon: 'FireIcon' },
    { value: 'badminton', label: '배드민턴', icon: 'FireIcon' },
    { value: 'running', label: '러닝', icon: 'FireIcon' },
    { value: 'cycling', label: '사이클', icon: 'FireIcon' },
  ];

  const skillLevelOptions = [
    { value: 'beginner', label: '초급' },
    { value: 'intermediate', label: '중급' },
    { value: 'advanced', label: '상급' },
    { value: 'expert', label: '최상급' },
  ];

  const toggleSport = (sport) => {
    setSelectedSports((prev) => (prev.includes(sport) ? prev.filter((s) => s !== sport) : [...prev, sport]));
    setSkillLevels(prev => {
      if (prev?.[sport]) return prev;
      return {
        ...(prev || {}),
        [sport]: 'beginner'
      };
    });
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setAvatarFile(file);
      setAvatarDeleted(false);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDeleteAvatar = async () => {
    setAvatarPreview(null);
    setAvatarFile(null);
    setAvatarUrl('');
    setAvatarDeleted(true);
  };

  const handleSaveProfile = async () => {
    if (!userId) {
      alert('로그인 상태를 확인할 수 없습니다.');
      return;
    }

    if (!String(username || '').trim()) {
      alert('사용자 이름을 입력해주세요.');
      return;
    }

    if (userType !== 'regular' && !String(orgName || '').trim()) {
      alert('시설/클럽/코트 이름을 입력해주세요.');
      return;
    }

    setIsLoading(true);
    try {
      try {
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
        console.log('[UserProfileManagement] saveProfile session', {
          userId,
          authed: Boolean(sessionData?.session?.user),
          sessionUserId: sessionData?.session?.user?.id,
          sessionError,
        });
      } catch (e) {
        console.log('[UserProfileManagement] saveProfile session check failed', e);
      }

      const usernameTrimmed = String(username || '').trim();
      const orgNameTrimmed = String(orgName || '').trim();
      const nextType = String(userType || 'regular');

      const bucket = 'avatars';
      let nextAvatarUrl = avatarUrl;
      let nextAvatarPath = avatarPath;

      if (avatarDeleted && nextAvatarPath) {
        const { error: removeError } = await supabase.storage.from(bucket).remove([nextAvatarPath]);
        if (removeError) {
          console.error('[UserProfileManagement] avatar remove error', {
            message: removeError?.message,
            name: removeError?.name,
            status: removeError?.status,
            cause: removeError?.cause,
          });
          alert(removeError?.message || '이미지 삭제에 실패했습니다.');
          setIsLoading(false);
          return;
        }
        nextAvatarUrl = '';
        nextAvatarPath = '';
      }

      if (avatarFile) {
        const source = await compressImageToUnder1MB(avatarFile);
        const mime = String(source?.type || avatarFile?.type || 'image/jpeg');
        const ext = mime === 'image/png' ? 'png' : mime === 'image/webp' ? 'webp' : 'jpg';
        const filePath = `${userId}/avatar-${Date.now()}.${ext}`;
        const { error: uploadError } = await supabase.storage
          .from(bucket)
          .upload(filePath, source, { upsert: true, contentType: mime });

        if (uploadError) {
          console.error('[UserProfileManagement] avatar upload error', {
            message: uploadError?.message,
            name: uploadError?.name,
            status: uploadError?.status,
            cause: uploadError?.cause,
          });
          alert(uploadError?.message || '이미지 업로드에 실패했습니다.');
          setIsLoading(false);
          return;
        }

        const { data: publicUrlData } = supabase.storage.from(bucket).getPublicUrl(filePath);
        nextAvatarUrl = String(publicUrlData?.publicUrl || '').trim();
        nextAvatarPath = filePath;
      }

      const profilePayload = {
        id: userId,
        role: 'clients',
        name: usernameTrimmed,
        type: nextType,
        organization_name: nextType !== 'regular' ? orgNameTrimmed : null,
        bio: String(bio || '').trim() || null,
        location: String(location || '').trim() || null,
        skill_levels: skillLevels || {},
        search_radius: Number.isFinite(Number(searchRadius)) ? Number(searchRadius) : 10,
        updated_at: new Date().toISOString(),
      };

      console.log('[UserProfileManagement] profiles.upsert payload', profilePayload);

      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .upsert(profilePayload)
        .select('id')
        .maybeSingle();

      if (profileError) {
        console.error('[UserProfileManagement] profiles.upsert error', {
          message: profileError?.message,
          details: profileError?.details,
          hint: profileError?.hint,
          code: profileError?.code,
        });
        alert(profileError?.message || '프로필 저장에 실패했습니다.');
        setIsLoading(false);
        return;
      }

      console.log('[UserProfileManagement] profiles.upsert ok', profileData);

      const authPayload = {
        data: {
          username: usernameTrimmed,
          type: nextType,
          organization_name: nextType !== 'regular' ? orgNameTrimmed : null,
          sports: selectedSports,
          bio: String(bio || '').trim() || null,
          location: String(location || '').trim() || null,
          skill_levels: skillLevels || {},
          search_radius: Number.isFinite(Number(searchRadius)) ? Number(searchRadius) : 10,
          avatar_url: nextAvatarUrl || null,
          avatar_path: nextAvatarPath || null,
        }
      };

      console.log('[UserProfileManagement] auth.updateUser payload', authPayload);

      const { data: authData, error: authError } = await supabase.auth.updateUser(authPayload);

      if (authError) {
        console.error('[UserProfileManagement] auth.updateUser error', {
          message: authError?.message,
          details: authError?.details,
          hint: authError?.hint,
          code: authError?.code,
        });
        alert(authError?.message || '프로필 저장에 실패했습니다.');
        setIsLoading(false);
        return;
      }

      console.log('[UserProfileManagement] auth.updateUser ok', {
        userId: authData?.user?.id,
      });

      alert('프로필이 저장되었습니다!');
      setAvatarUrl(nextAvatarUrl);
      setAvatarPath(nextAvatarPath);
      setAvatarFile(null);
      setAvatarDeleted(false);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isHydrated) {
    return null;
  }

  const tabs = [
    { id: 'profile', label: '프로필', icon: 'UserIcon' },
  ];

  return (
    <div className="min-h-screen bg-background">
      <div className="hidden md:block md:pl-[240px]">
        <div className="max-w-6xl mx-auto px-6 py-8">
          <div className="flex">
            <main className="flex-1 min-w-0">
              <div className="max-w-3xl">
            {activeTab === 'profile' && (
              <div className="bg-card border border-border rounded-xl p-6 space-y-8">
                <div>
                  <h2 className="text-2xl font-bold font-heading text-foreground mb-2">프로필 편집</h2>
                  <p className="text-sm text-muted-foreground caption">프로필 정보와 스포츠 설정을 관리합니다</p>
                </div>

                <div className="flex items-center gap-6">
                  <div className="relative">
                    <div className="w-24 h-24 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-3xl font-bold overflow-hidden">
                      {avatarPreview ? (
                        <img src={avatarPreview} alt="Avatar" className="w-full h-full object-cover" />
                      ) : avatarUrl ? (
                        <img src={avatarUrl} alt="Avatar" className="w-full h-full object-cover" />
                      ) : (
                        username.charAt(0)
                      )}
                    </div>
                    <label className="absolute bottom-0 right-0 w-8 h-8 bg-card border-2 border-background rounded-full flex items-center justify-center cursor-pointer hover:bg-muted transition-smooth">
                      <Icon name="CameraIcon" size={16} className="text-foreground" />
                      <input type="file" accept="image/*" onChange={handleAvatarChange} className="hidden" />
                    </label>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">{username}</h3>
                    <p className="text-sm text-muted-foreground caption mb-2">{location}</p>
                    <button className="text-sm text-primary hover:underline" type="button">
                      프로필 사진 변경
                    </button>
                    {(avatarUrl || avatarPreview) && (
                      <button
                        className="ml-3 text-sm text-muted-foreground hover:underline"
                        type="button"
                        onClick={handleDeleteAvatar}
                      >
                        이미지 삭제
                      </button>
                    )}
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-foreground">이메일</label>
                    <input
                      type="text"
                      value={email}
                      readOnly
                      className="w-full px-4 py-3 rounded-lg border border-border bg-muted text-muted-foreground focus:outline-none"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-foreground">사용자 이름</label>
                    <input
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-foreground">회원 유형</label>
                    <select
                      value={userType}
                      onChange={(e) => setUserType(e.target.value)}
                      className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      <option value="regular">일반</option>
                      <option value="practice">연습장 운영자</option>
                      <option value="club">클럽 운영자</option>
                      <option value="court">코트 운영자</option>
                    </select>
                  </div>

                  {userType !== 'regular' && (
                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-foreground">시설/클럽/코트 이름</label>
                      <input
                        type="text"
                        value={orgName}
                        onChange={(e) => setOrgName(e.target.value)}
                        className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>
                  )}

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-foreground">소개</label>
                    <textarea
                      value={bio}
                      onChange={(e) => setBio(e.target.value)}
                      rows={3}
                      maxLength={200}
                      className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                    />
                    <p className="text-xs text-muted-foreground text-right caption">{bio.length} / 200자</p>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-foreground">활동 지역</label>
                    <input
                      type="text"
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                </div>

                <button
                  onClick={handleSaveProfile}
                  disabled={isLoading}
                  className="flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-primary text-primary-foreground font-semibold hover:shadow-elevation-2 transition-smooth touch-target disabled:opacity-50 disabled:cursor-not-allowed active:scale-97"
                  type="button"
                >
                  {isLoading ? (
                    <>
                      <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
                      저장 중...
                    </>
                  ) : (
                    <>
                      <Icon name="CheckCircleIcon" size={20} />
                      저장하기
                    </>
                  )}
                </button>
              </div>
            )}
              </div>
            </main>
          </div>
        </div>
      </div>

      <div className="md:hidden min-h-screen pb-20">
        <div className="sticky top-0 z-[200] bg-card border-b border-border px-6 py-4">
          <h1 className="text-xl font-bold font-heading text-foreground">프로필 편집</h1>
        </div>

        <div className="p-4">
          {activeTab === 'profile' && (
            <div className="space-y-8 bg-card border border-border rounded-xl p-4">
              <div className="flex flex-col items-center gap-4">
                <div className="relative">
                  <div className="w-24 h-24 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-3xl font-bold overflow-hidden">
                    {avatarPreview ? (
                      <img src={avatarPreview} alt="Avatar" className="w-full h-full object-cover" />
                    ) : avatarUrl ? (
                      <img src={avatarUrl} alt="Avatar" className="w-full h-full object-cover" />
                    ) : (
                      username.charAt(0)
                    )}
                  </div>
                  <label className="absolute bottom-0 right-0 w-8 h-8 bg-card border-2 border-background rounded-full flex items-center justify-center cursor-pointer hover:bg-muted transition-smooth">
                    <Icon name="CameraIcon" size={16} className="text-foreground" />
                    <input type="file" accept="image/*" onChange={handleAvatarChange} className="hidden" />
                  </label>
                </div>
                {(avatarUrl || avatarPreview) && (
                  <button
                    className="text-sm text-muted-foreground hover:underline"
                    type="button"
                    onClick={handleDeleteAvatar}
                  >
                    이미지 삭제
                  </button>
                )}
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-foreground">이메일</label>
                  <input
                    type="text"
                    value={email}
                    readOnly
                    className="w-full px-4 py-3 rounded-lg border border-border bg-muted text-muted-foreground focus:outline-none"
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-foreground">사용자 이름</label>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-foreground">회원 유형</label>
                  <select
                    value={userType}
                    onChange={(e) => setUserType(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="regular">일반</option>
                    <option value="practice">연습장 운영자</option>
                    <option value="club">클럽 운영자</option>
                    <option value="court">코트 운영자</option>
                  </select>
                </div>

                {userType !== 'regular' && (
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-foreground">시설/클럽/코트 이름</label>
                    <input
                      type="text"
                      value={orgName}
                      onChange={(e) => setOrgName(e.target.value)}
                      className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-foreground">소개</label>
                  <textarea
                    value={bio}
                    onChange={(e) => setBio(e.target.value)}
                    rows={3}
                    maxLength={200}
                    className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-foreground">활동 지역</label>
                  <input
                    type="text"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
              </div>

              <button
                onClick={handleSaveProfile}
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-primary text-primary-foreground font-semibold hover:shadow-elevation-2 transition-smooth touch-target disabled:opacity-50 disabled:cursor-not-allowed active:scale-97"
                type="button"
              >
                {isLoading ? (
                  <>
                    <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
                    저장 중...
                  </>
                ) : (
                  <>
                    <Icon name="CheckCircleIcon" size={20} />
                    저장하기
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      <BottomTabNavigation userRole="player" />
    </div>
  );
};

export default UserProfileManagementPage;
